import { useContext } from "react";
import { Navigate } from "react-router-dom";
import { UserContext } from "../../../utils/contexts/UserContext";
import { getUserPermissions } from "../../../utils/permissions";

/**
 * RouteGuard - Protects routes based on user permissions
 *
 * Usage:
 *   <Route
 *     path="test/classes"
 *     element={
 *       <RouteGuard requiredPermissions={['tests.results.view_groups', 'tests.results.view_all']}>
 *         <TestResultsPage />
 *       </RouteGuard>
 *     }
 *   />
 *
 * @param {string[]} requiredPermissions - Array of permission strings (user needs at least one)
 * @param {ReactNode} children - The component to render if user has access
 * @param {string} fallbackPath - Path to redirect to if access is denied. If omitted, students go to /test and everyone else goes to /.
 */
export default function RouteGuard({
  requiredPermissions = [],
  children,
  fallbackPath,
}) {
  const { user } = useContext(UserContext);

  if (!user?.uuid) {
    return <Navigate to="/login" replace />;
  }

  const allPerms = getUserPermissions(user);

  const hasAccess = requiredPermissions.some((perm) =>
    allPerms.includes(perm)
  );

  if (!hasAccess) {
    const userRoleCode = user?.roles?.[0]?.code;
    const resolvedFallback =
      fallbackPath || (userRoleCode === "student" ? "/test" : "/");
    return <Navigate to={resolvedFallback} replace />;
  }

  return children;
}
